import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowLeft, Star, GripVertical, List, WifiOff } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

const API = process.env.REACT_APP_API_URL;

const METHODS = ['법인카드', '세금계산서', '현금영수증', '영수증', '개인카드'];
const RECEIPT_OPTIONS = ['수령', '미수령', '-'];
const EXECUTORS = ['김태연', '이정찬', '김상경'];

const fmt = (n) => (n || 0).toLocaleString('ko-KR');

const emptyItem = () => ({
  itemName: '', itemCategory: '', description: '',
  unitPrice: 0, quantity: 1, unit: 1,
  budgetAmount: 0, spentAmount: 0,
  executor: '', spentDate: '', paymentMethod: '', receiptYn: '-', note: '',
  isImportant: false,
});

export default function PaperAdminBudgetPage() {
  const { id } = useParams();
  const me = useSelector(state => state.auth.me);
  const isSuperAdmin = me?.superAdminYn === 'Y';
  const [project, setProject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [newCatName, setNewCatName] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);
  const [focusedCell, setFocusedCell] = useState(null); // 포맷/입력 전환용
  const [dragItem, setDragItem] = useState(null);       // { catId, itemId }
  const [dragOverItem, setDragOverItem] = useState(null); // { catId, itemId }
  const [expandedSpending, setExpandedSpending] = useState(null); // itemId

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ─── 실시간 협업 ───
  const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const [peerCells, setPeerCells] = useState({}); // { cellId: { name, color } }
  const versionRef = useRef(0);
  const peersRef = useRef('[]');
  const cellsRef = useRef('{}');

  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc();
    const prov = new WebsocketProvider('wss://api.urpink.co.kr/yjs', `event-budget-${id}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (provider.wsconnected) setConnected(true);
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    provider.awareness.setLocalStateField('user', {
      name: me?.nickName || me?.name || 'Unknown',
      color: COLORS[(me?.id || 0) % COLORS.length],
      profileImage: me?.profileImage || null,
    });
    const ymap = ydoc.getMap('sync');
    const onMapChange = () => {
      const v = ymap.get('version') || 0;
      if (v > versionRef.current) { versionRef.current = v; fetchData(); }
    };
    ymap.observe(onMapChange);
    const awarenessHandler = () => {
      const myName = me?.nickName || me?.name || 'Unknown';
      const seen = new Set();
      const users = [];
      const cells = {};
      provider.awareness.getStates().forEach((state) => {
        if (!state.user) return;
        if (!seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ name: state.user.name, color: state.user.color, profileImage: state.user.profileImage, isMe: state.user.name === myName });
        }
        if (state.user.editingCell && state.user.name !== myName) {
          cells[state.user.editingCell] = { name: state.user.name, color: state.user.color };
        }
      });
      const idx = users.findIndex(u => u.isMe);
      if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      const pStr = JSON.stringify(users);
      const cStr = JSON.stringify(cells);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
      if (cStr !== cellsRef.current) { cellsRef.current = cStr; setPeerCells(cells); }
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();
    return () => { provider.destroy(); ydoc.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ydoc, me]);

  const notifyChange = () => {
    const ymap = ydoc.getMap('sync');
    versionRef.current = (ymap.get('version') || 0) + 1;
    ymap.set('version', versionRef.current);
  };

  const setEditingCell = (cellId) => {
    provider.awareness.setLocalStateField('user', {
      ...provider.awareness.getLocalState()?.user,
      editingCell: cellId,
    });
  };

  const EMPTY_STYLE = useMemo(() => ({}), []);
  const getCellStyle = useCallback((cellId) => {
    const peer = peerCells[cellId];
    if (!peer) return EMPTY_STYLE;
    return { boxShadow: `inset 0 0 0 2px ${peer.color}`, position: 'relative' };
  }, [peerCells, EMPTY_STYLE]);

  const CellCursor = ({ cellId }) => {
    const peer = peerCells[cellId];
    if (!peer) return null;
    return (
      <div className="absolute -top-5 left-0 z-10 pointer-events-none"
        style={{ animation: 'yCursorFadeIn 0.15s ease-out' }}>
        <span className="text-[10px] font-semibold text-white px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{ background: peer.color, borderRadius: '3px 3px 3px 0' }}>{peer.name}</span>
      </div>
    );
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, budgetRes] = await Promise.all([
        axios.get(`${API}/api/event-layout/projects/${id}`, { headers }),
        axios.get(`${API}/api/event-manage/projects/${id}/budget`, { headers }),
      ]);
      setProject(projRes.data);
      setCategories(budgetRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 대분류 추가
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await axios.post(`${API}/api/event-manage/projects/${id}/budget/categories`, { name: newCatName }, { headers });
      setCategories(prev => [...prev, res.data]);
      setNewCatName('');
      setShowNewCat(false);
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 대분류 삭제
  const deleteCategory = async (catId) => {
    if (!window.confirm('대분류를 삭제하면 모든 항목도 삭제됩니다. 계속하시겠습니까?')) return;
    try {
      await axios.delete(`${API}/api/event-manage/budget/categories/${catId}`, { headers });
      setCategories(prev => prev.filter(c => c.id !== catId));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 대분류명 수정
  const updateCategoryName = async (catId, name) => {
    try {
      await axios.put(`${API}/api/event-manage/budget/categories/${catId}`, { name }, { headers });
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 항목 추가
  const addItem = async (catId) => {
    try {
      const cat = categories.find(c => c.id === catId);
      const nextOrder = (cat?.items || []).length;
      const res = await axios.post(`${API}/api/event-manage/budget/categories/${catId}/items`, { ...emptyItem(), order: nextOrder }, { headers });
      setCategories(prev => prev.map(c =>
        c.id === catId ? { ...c, items: [...(c.items || []), res.data] } : c
      ));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 항목 필드 로컬 업데이트 (단가/수량/회 변경 시 예산 자동계산)
  const updateItemLocal = (catId, itemId, field, value) => {
    setCategories(prev => prev.map(c =>
      c.id === catId ? {
        ...c,
        items: c.items.map(it => {
          if (it.id !== itemId) return it;
          const updated = { ...it, [field]: value };
          if (['unitPrice', 'quantity', 'unit'].includes(field)) {
            const p = field === 'unitPrice' ? value : (it.unitPrice || 0);
            const q = field === 'quantity'  ? value : (it.quantity  || 1);
            const u = field === 'unit'      ? value : (Number(it.unit) || 1);
            updated.budgetAmount = p * q * u;
          }
          return updated;
        })
      } : c
    ));
  };

  // 항목 저장 (blur) — order 필드는 제외해서 순서 보호
  const saveItem = async (item) => {
    const { order, id, eventBudgetCategoryId, createdAt, updatedAt, ...fields } = item;
    try {
      await axios.put(`${API}/api/event-manage/budget/items/${item.id}`, fields, { headers });
      notifyChange();
    } catch { alert('저장 중 오류가 발생했습니다.'); }
  };

  // 항목 삭제
  const deleteItem = async (catId, itemId) => {
    try {
      await axios.delete(`${API}/api/event-manage/budget/items/${itemId}`, { headers });
      setCategories(prev => prev.map(c =>
        c.id === catId ? { ...c, items: c.items.filter(it => it.id !== itemId) } : c
      ));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const toggleCollapse = (catId) => setCollapsed(prev => ({ ...prev, [catId]: !prev[catId] }));

  // 세부지출
  const toggleSpending = (itemId) => setExpandedSpending(prev => prev === itemId ? null : itemId);

  const addSpending = async (catId, itemId) => {
    try {
      const res = await axios.post(`${API}/api/event-manage/budget/items/${itemId}/spendings`, { amount: 0 }, { headers });
      setCategories(prev => prev.map(c => c.id !== catId ? c : {
        ...c, items: c.items.map(it => it.id !== itemId ? it : { ...it, spendings: [...(it.spendings || []), res.data] })
      }));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  const updateSpendingLocal = (catId, itemId, spendingId, field, value) => {
    setCategories(prev => prev.map(c => c.id !== catId ? c : {
      ...c, items: c.items.map(it => it.id !== itemId ? it : {
        ...it, spendings: (it.spendings || []).map(sp => sp.id !== spendingId ? sp : { ...sp, [field]: value })
      })
    }));
  };

  const saveSpending = async (spending) => {
    const { id, eventBudgetItemId, createdAt, updatedAt, ...fields } = spending;
    try {
      await axios.put(`${API}/api/event-manage/budget/spendings/${spending.id}`, fields, { headers });
      notifyChange();
    } catch { alert('저장 중 오류가 발생했습니다.'); }
  };

  const deleteSpending = async (catId, itemId, spendingId) => {
    try {
      await axios.delete(`${API}/api/event-manage/budget/spendings/${spendingId}`, { headers });
      setCategories(prev => prev.map(c => c.id !== catId ? c : {
        ...c, items: c.items.map(it => it.id !== itemId ? it : {
          ...it, spendings: (it.spendings || []).filter(sp => sp.id !== spendingId)
        })
      }));
      notifyChange();
    } catch { alert('오류가 발생했습니다.'); }
  };

  // 드래그 핸들러
  const onItemDragStart = (catId, itemId) => setDragItem({ catId, itemId });
  const onItemDragOver = (e, catId, itemId) => { e.preventDefault(); setDragOverItem({ catId, itemId }); };
  const onItemDragEnd = () => { setDragItem(null); setDragOverItem(null); };
  const onItemDrop = (catId, targetId) => {
    if (!dragItem || dragItem.catId !== catId || dragItem.itemId === targetId) return;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const arr = [...cat.items];
    const fromIdx = arr.findIndex(it => it.id === dragItem.itemId);
    const toIdx   = arr.findIndex(it => it.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    const reordered = arr.map((it, i) => ({ id: it.id, order: i }));
    setCategories(prev => prev.map(c =>
      c.id !== catId ? c : { ...c, items: arr.map((it, i) => ({ ...it, order: i })) }
    ));
    axios.put(`${API}/api/event-manage/budget/reorder-items`, reordered, { headers }).then(() => notifyChange()).catch(console.error);
    setDragItem(null); setDragOverItem(null);
  };

  // 세부지출이 있으면 합계, 없으면 수기 지출금액
  const getSpent = (it) => {
    const sps = it.spendings || [];
    return sps.length > 0 ? sps.reduce((s, sp) => s + (Number(sp.amount) || 0), 0) : (Number(it.spentAmount) || 0);
  };

  // 합계 계산
  const calcTotals = (items) => {
    const totalBudget = items.reduce((s, it) => s + (Number(it.budgetAmount) || 0), 0);
    const totalSpent = items.reduce((s, it) => s + getSpent(it), 0);
    return { totalBudget, totalSpent, remaining: totalBudget - totalSpent };
  };

  const grandBudget = categories.reduce((s, c) => s + (c.items || []).reduce((ss, it) => ss + (Number(it.budgetAmount) || 0), 0), 0);
  const grandSpent = categories.reduce((s, c) => s + (c.items || []).reduce((ss, it) => ss + getSpent(it), 0), 0);

  // 인접한 같은 값끼리 rowspan 계산 (범위 제한 가능)
  const calcSpans = (items, getKey, ranges) => {
    const spans = Array(items.length).fill(1);
    const segments = ranges || [[0, items.length]];
    for (const [start, end] of segments) {
      let i = start;
      while (i < end) {
        const key = getKey(items[i]);
        let j = i + 1;
        while (j < end && getKey(items[j]) === key && key !== '') j++;
        if (j > i + 1) {
          spans[i] = j - i;
          for (let k = i + 1; k < j; k++) spans[k] = 0;
        }
        i = j;
      }
    }
    return spans;
  };

  // 논리적 그룹: 이름 있는 행 + 후속 빈이름/같은이름 행
  const getLogicalRanges = (items, getKey) => {
    const ranges = [];
    let start = 0;
    while (start < items.length) {
      const name = getKey(items[start]);
      if (name === '') { ranges.push([start, start + 1]); start++; continue; }
      let end = start + 1;
      while (end < items.length) {
        const next = getKey(items[end]);
        if (next === '' || next === name) end++;
        else break;
      }
      ranges.push([start, end]);
      start = end;
    }
    return ranges;
  };

  const getRowSpans = (items) => {
    // 항목 — 이름 있는 행 + 후속 빈이름/같은이름 행 합침
    const nameRanges = getLogicalRanges(items, it => it.itemName || '');
    const nameSpans = Array(items.length).fill(1);
    for (const [s, e] of nameRanges) {
      if (e - s > 1) {
        nameSpans[s] = e - s;
        for (let k = s + 1; k < e; k++) nameSpans[k] = 0;
      }
    }
    // 구분 — 같은 항목 그룹 안에서만 합침
    const catSpans = calcSpans(items, it => it.itemCategory || '', nameRanges);
    return { nameSpans, catSpans };
  };

  const cellCls = "border border-white/10 px-2 py-1.5 text-xs";
  const inputCls = "w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5";
  const numInputCls = "w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5 text-right";
  const centerInputCls = "w-full bg-transparent text-white text-xs outline-none focus:bg-white/5 rounded px-1 py-0.5 text-center";
  const centerSelectCls = "w-full bg-transparent text-white text-xs outline-none rounded px-1 py-0.5 cursor-pointer text-center";

  if (loading) return <div className="bg-[#0d0b14] min-h-screen flex items-center justify-center text-white/40">로딩 중...</div>;

  return (
    <div className="bg-[#0d0b14] min-h-screen pb-20">
      {/* 헤더 */}
      <section className="pt-10 pb-6">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-1">
            <Link to={`${PAPER_BASE}/layouts`} className="text-white/40 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <h1 className="text-white text-2xl font-bold">
              {project?.title || ''} 예산서
            </h1>
            <div className="flex items-center gap-2 ml-auto">
              <div className="flex -space-x-1.5">
                {peers.slice(0, 6).map((p, i) => (
                  <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 overflow-hidden ${p.isMe ? 'border-violet-400' : 'border-[#0d0b14]'}`}
                    style={{ backgroundColor: p.profileImage ? 'transparent' : p.color }} title={p.isMe ? `${p.name} (나)` : p.name}>
                    {p.profileImage ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" /> : p.name?.charAt(0)}
                  </div>
                ))}
              </div>
              {!connected && <WifiOff size={14} className="text-red-400" title="오프라인" />}
            </div>
          </div>
          <div className="flex items-center gap-6 mt-3 ml-7">
            <div className="text-sm">
              <span className="text-white/40">총 예산</span>
              <span className="text-white font-semibold ml-2">{fmt(grandBudget)}원</span>
            </div>
            <div className="text-sm">
              <span className="text-white/40">총 지출</span>
              <span className="text-amber-400 font-semibold ml-2">{fmt(grandSpent)}원</span>
            </div>
            <div className="text-sm">
              <span className="text-white/40">잔여</span>
              <span className={`font-semibold ml-2 ${grandBudget - grandSpent < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {fmt(grandBudget - grandSpent)}원
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 space-y-6">

        {/* 대분류 목록 */}
        {categories.map(cat => {
          const items = cat.items || [];
          const { totalBudget, totalSpent, remaining } = calcTotals(items);
          const { nameSpans: baseNameSpans, catSpans: baseCatSpans } = getRowSpans(items);
          // 세부지출 확장 시 추가 행 수 계산하여 rowspan 조정
          const extraRows = items.map(it =>
            expandedSpending === it.id ? (it.spendings || []).length + 1 : 0
          );
          const nameSpans = [...baseNameSpans];
          const catSpans = [...baseCatSpans];
          for (let i = 0; i < items.length; i++) {
            if (nameSpans[i] > 0) {
              let extra = 0;
              for (let j = i; j < i + baseNameSpans[i]; j++) extra += extraRows[j];
              nameSpans[i] += extra;
            }
            if (catSpans[i] > 0) {
              let extra = 0;
              for (let j = i; j < i + baseCatSpans[i]; j++) extra += extraRows[j];
              catSpans[i] += extra;
            }
          }
          const isCollapsed = collapsed[cat.id];

          return (
            <div key={cat.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              {/* 대분류 헤더 */}
              <div className="flex items-center justify-between px-4 py-3 bg-white/5 border-b border-white/10">
                <div className="flex items-center gap-2 flex-1">
                  <button onClick={() => toggleCollapse(cat.id)} className="text-white/40 hover:text-white">
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </button>
                  {isSuperAdmin ? (
                    <input
                      defaultValue={cat.name}
                      onBlur={e => updateCategoryName(cat.id, e.target.value)}
                      className="bg-transparent text-white font-semibold text-sm outline-none focus:bg-white/5 rounded px-2 py-1 min-w-0"
                    />
                  ) : (
                    <span className="text-white font-semibold text-sm px-2 py-1">{cat.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs mr-3">
                  <span className="text-white/40">예산 <span className="text-white font-medium">{fmt(totalBudget)}</span></span>
                  <span className="text-white/40">지출 <span className="text-amber-400 font-medium">{fmt(totalSpent)}</span></span>
                  <span className="text-white/40">잔여 <span className={`font-medium ${remaining < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{fmt(remaining)}</span></span>
                </div>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => addItem(cat.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/15 text-white/70 text-xs rounded-lg transition-colors">
                      <Plus size={12} /> 행 추가
                    </button>
                    <button onClick={() => deleteCategory(cat.id)}
                      className="text-white/20 hover:text-red-400 transition-colors p-1">
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              {/* 테이블 */}
              {!isCollapsed && (
                <div className="overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-white/35"
                  onDragOver={e => e.preventDefault()}>
                  <table className="text-xs border-collapse">
                    <thead>
                      <tr className="text-white/40 bg-white/3 text-center">
                        <th className={`${cellCls} w-12`}></th>
                        <th className={`${cellCls} w-20`}>항목</th>
                        <th className={`${cellCls} min-w-[110px]`}>구분</th>
                        <th className={`${cellCls} min-w-[200px]`}>내용</th>
                        <th className={`${cellCls} w-24`}>단가</th>
                        <th className={`${cellCls} w-14`}>수량</th>
                        <th className={`${cellCls} w-14`}>회</th>
                        <th className={`${cellCls} w-24`}>예산</th>
                        <th className={`${cellCls} w-24`}>지출금액</th>
                        <th className={`${cellCls} w-20`}>집행인</th>
                        <th className={`${cellCls} w-[88px]`}>지출일자</th>
                        <th className={`${cellCls} w-24`}>지출방법</th>
                        <th className={`${cellCls} w-14`}>영수증</th>
                        <th className={`${cellCls} min-w-[100px]`}>비고</th>
                        <th className={`${cellCls} w-7`}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={15} className="text-center text-white/30 py-6">
                            항목이 없습니다. "행 추가"를 눌러 추가하세요.
                          </td>
                        </tr>
                      ) : items.map((it, idx) => {
                        const span = nameSpans[idx];
                        const catSpan = catSpans[idx];
                        const isDragging = dragItem?.catId === cat.id && dragItem?.itemId === it.id;
                        const isDragOver = dragOverItem?.catId === cat.id && dragOverItem?.itemId === it.id;
                        const sps = it.spendings || [];
                        const hasSp = sps.length > 0;
                        const spSum = hasSp ? sps.reduce((s, sp) => s + (Number(sp.amount) || 0), 0) : 0;
                        return (
                          <Fragment key={it.id}>
                          <tr
                            draggable={isSuperAdmin}
                            onDragStart={isSuperAdmin ? () => onItemDragStart(cat.id, it.id) : undefined}
                            onDragOver={isSuperAdmin ? (e => onItemDragOver(e, cat.id, it.id)) : undefined}
                            onDrop={isSuperAdmin ? (() => onItemDrop(cat.id, it.id)) : undefined}
                            onDragEnd={isSuperAdmin ? onItemDragEnd : undefined}
                            className={`transition-colors ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-t-2 border-violet-400' : ''} ${it.isImportant ? 'bg-red-500/10' : getSpent(it) > 0 ? 'bg-yellow-500/10' : 'hover:bg-white/3'}`}>
                            <td className={`${cellCls} text-center`}>
                              {isSuperAdmin ? (
                                <div className="flex items-center justify-center gap-1">
                                  <GripVertical size={11} className="text-white/15 cursor-grab active:cursor-grabbing shrink-0" />
                                  <button onClick={() => { updateItemLocal(cat.id, it.id, 'isImportant', !it.isImportant); saveItem({ ...it, isImportant: !it.isImportant }); }}
                                    className="transition-colors">
                                    <Star size={12} className={it.isImportant ? 'text-amber-400 fill-amber-400' : 'text-white/15 hover:text-white/40'} />
                                  </button>
                                </div>
                              ) : (
                                <Star size={12} className={it.isImportant ? 'text-amber-400 fill-amber-400 mx-auto' : 'text-white/10 mx-auto'} />
                              )}
                            </td>
                            {span > 0 && (
                              <td className={`${cellCls} align-middle`} rowSpan={span}>
                                {isSuperAdmin ? (
                                  <input
                                    value={it.itemName || ''}
                                    onChange={e => updateItemLocal(cat.id, it.id, 'itemName', e.target.value)}
                                    onBlur={() => saveItem(it)}
                                    className={inputCls}
                                    placeholder="항목명"
                                  />
                                ) : (
                                  <span className="text-white text-xs px-1">{it.itemName || ''}</span>
                                )}
                              </td>
                            )}
                            {catSpan > 0 && (
                              <td className={`${cellCls} align-middle`} rowSpan={catSpan}>
                                {isSuperAdmin ? (
                                  <input value={it.itemCategory || ''} onChange={e => updateItemLocal(cat.id, it.id, 'itemCategory', e.target.value)} onBlur={() => saveItem(it)} className={inputCls} placeholder="구분" />
                                ) : (
                                  <span className="text-white text-xs px-1">{it.itemCategory || ''}</span>
                                )}
                              </td>
                            )}
                            <td className={`${cellCls} max-w-[200px]`}>
                              {isSuperAdmin ? (
                                focusedCell === `${it.id}-desc` ? (
                                  <div className="overflow-x-auto [&::-webkit-scrollbar]:h-[2px] [&::-webkit-scrollbar-thumb]:bg-white/20">
                                    <input value={it.description || ''} onChange={e => updateItemLocal(cat.id, it.id, 'description', e.target.value)}
                                      onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                      className="bg-white/5 text-white text-xs outline-none rounded px-1 py-0.5 whitespace-nowrap" style={{ minWidth: '100%' }}
                                      autoFocus />
                                  </div>
                                ) : (
                                  <div onClick={() => setFocusedCell(`${it.id}-desc`)}
                                    className="truncate text-white text-xs px-1 py-0.5 cursor-text rounded hover:bg-white/5 min-h-[18px]"
                                    title={it.description || ''}>
                                    {it.description || <span className="text-white/20">내용</span>}
                                  </div>
                                )
                              ) : (
                                <div className="truncate text-white text-xs px-1 py-0.5 min-h-[18px]" title={it.description || ''}>
                                  {it.description || ''}
                                </div>
                              )}
                            </td>
                            {/* 단가 */}
                            <td className={cellCls}>
                              {isSuperAdmin ? (
                                <input
                                  type="text"
                                  value={focusedCell === `${it.id}-unitPrice` ? String(it.unitPrice || 0) : fmt(it.unitPrice)}
                                  onChange={e => updateItemLocal(cat.id, it.id, 'unitPrice', Number(e.target.value.replace(/,/g, '')) || 0)}
                                  onFocus={() => setFocusedCell(`${it.id}-unitPrice`)}
                                  onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                  className={numInputCls} />
                              ) : (
                                <span className="text-white text-xs text-right block px-1">{fmt(it.unitPrice)}</span>
                              )}
                            </td>
                            <td className={cellCls}>
                              {isSuperAdmin ? (
                                <input type="number" min="1" value={it.quantity || 1} onChange={e => updateItemLocal(cat.id, it.id, 'quantity', Number(e.target.value) || 1)} onBlur={() => saveItem(it)} className={centerInputCls} />
                              ) : (
                                <span className="text-white text-xs text-center block px-1">{it.quantity || 1}</span>
                              )}
                            </td>
                            {/* 회 */}
                            <td className={cellCls}>
                              {isSuperAdmin ? (
                                <input type="number" min="1" value={it.unit || 1} onChange={e => updateItemLocal(cat.id, it.id, 'unit', Number(e.target.value) || 1)} onBlur={() => saveItem(it)} className={centerInputCls} />
                              ) : (
                                <span className="text-white text-xs text-center block px-1">{it.unit || 1}</span>
                              )}
                            </td>
                            {/* 예산 */}
                            <td className={cellCls}>
                              {isSuperAdmin ? (
                                <input
                                  type="text"
                                  value={focusedCell === `${it.id}-budgetAmount` ? String(it.budgetAmount || 0) : fmt(it.budgetAmount)}
                                  onChange={e => updateItemLocal(cat.id, it.id, 'budgetAmount', Number(e.target.value.replace(/,/g, '')) || 0)}
                                  onFocus={() => setFocusedCell(`${it.id}-budgetAmount`)}
                                  onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                  className={numInputCls} />
                              ) : (
                                <span className="text-white text-xs text-right block px-1">{fmt(it.budgetAmount)}</span>
                              )}
                            </td>
                            {/* 지출금액 */}
                            <td className={cellCls}>
                              <div className="flex items-center gap-0.5">
                                {hasSp ? (
                                  <span className="text-xs text-right flex-1 font-medium px-1 cursor-default"
                                    style={{ color: spSum > (it.budgetAmount || 0) ? '#f87171' : spSum > 0 && spSum < (it.budgetAmount || 0) ? '#60a5fa' : '#ffffff' }}
                                    title="세부지출 합계">{fmt(spSum)}</span>
                                ) : (
                                  <input
                                    type="text"
                                    value={focusedCell === `${it.id}-spentAmount` ? String(it.spentAmount || 0) : fmt(it.spentAmount)}
                                    onChange={e => updateItemLocal(cat.id, it.id, 'spentAmount', Number(e.target.value.replace(/,/g, '')) || 0)}
                                    onFocus={() => setFocusedCell(`${it.id}-spentAmount`)}
                                    onBlur={() => { setFocusedCell(null); saveItem(it); }}
                                    className={`${numInputCls} flex-1`}
                                    style={focusedCell !== `${it.id}-spentAmount` ? {
                                      color: (it.spentAmount || 0) > (it.budgetAmount || 0) ? '#f87171'
                                           : (it.spentAmount || 0) < (it.budgetAmount || 0) && (it.spentAmount || 0) > 0 ? '#60a5fa'
                                           : undefined
                                    } : {}} />
                                )}
                                <button onClick={() => toggleSpending(it.id)}
                                  className={`shrink-0 transition-colors ${expandedSpending === it.id ? 'text-violet-400' : hasSp ? 'text-amber-400/60 hover:text-amber-400' : 'text-white/15 hover:text-violet-400'}`}>
                                  <List size={12} />
                                </button>
                              </div>
                            </td>
                            {/* 집행인 - 선택 */}
                            <td className={`${cellCls} text-center`}>
                              <select value={it.executor || ''} onChange={e => { updateItemLocal(cat.id, it.id, 'executor', e.target.value); saveItem({ ...it, executor: e.target.value }); }}
                                className={centerSelectCls}>
                                <option value="" className="bg-[#1a1428]">-</option>
                                {EXECUTORS.map(ex => <option key={ex} value={ex} className="bg-[#1a1428]">{ex}</option>)}
                              </select>
                            </td>
                            {/* 지출일자 - 좁게, 중앙 */}
                            <td className={`${cellCls} w-[88px] text-center`}>
                              <input type="date" value={it.spentDate || ''} onChange={e => updateItemLocal(cat.id, it.id, 'spentDate', e.target.value)} onBlur={() => saveItem(it)}
                                className="w-full bg-transparent text-white text-[11px] outline-none focus:bg-white/5 rounded px-0.5 py-0.5 text-center [color-scheme:dark]" />
                            </td>
                            <td className={`${cellCls} text-center`}>
                              <select value={it.paymentMethod || ''} onChange={e => { updateItemLocal(cat.id, it.id, 'paymentMethod', e.target.value); saveItem({ ...it, paymentMethod: e.target.value }); }}
                                className={centerSelectCls}>
                                <option value="" className="bg-[#1a1428]">-</option>
                                {METHODS.map(m => <option key={m} value={m} className="bg-[#1a1428]">{m}</option>)}
                              </select>
                            </td>
                            <td className={`${cellCls} text-center`}>
                              <select value={it.receiptYn || '-'} onChange={e => { updateItemLocal(cat.id, it.id, 'receiptYn', e.target.value); saveItem({ ...it, receiptYn: e.target.value }); }}
                                className="w-full bg-transparent text-white text-xs outline-none rounded pl-1 pr-4 py-0.5 cursor-pointer text-center appearance-none">
                                {RECEIPT_OPTIONS.map(r => <option key={r} value={r} className="bg-[#1a1428]">{r}</option>)}
                              </select>
                            </td>
                            <td className={cellCls}>
                              <input value={it.note || ''} onChange={e => updateItemLocal(cat.id, it.id, 'note', e.target.value)} onBlur={() => saveItem(it)} className={inputCls} placeholder="비고" />
                            </td>
                            <td className={`${cellCls} text-center`}>
                              {isSuperAdmin && (
                                <button onClick={() => deleteItem(cat.id, it.id)} className="text-white/20 hover:text-red-400 transition-colors">
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </td>
                          </tr>
                          {expandedSpending === it.id && (
                            <>
                              {sps.map(sp => (
                                <tr key={`sp-${sp.id}`} className="bg-violet-500/5">
                                  <td className={`${cellCls} text-center text-violet-400/30`}>
                                    <span className="text-[10px]">└</span>
                                  </td>
                                  <td colSpan={5} className={cellCls}>
                                    <input value={sp.description || ''} onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'description', e.target.value)}
                                      onBlur={() => saveSpending(sp)} className={inputCls} placeholder="세부 내용" />
                                  </td>
                                  <td className={cellCls}>
                                    <input type="text"
                                      value={focusedCell === `sp-${sp.id}` ? String(sp.amount || 0) : fmt(sp.amount)}
                                      onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'amount', Number(e.target.value.replace(/,/g,'')) || 0)}
                                      onFocus={() => setFocusedCell(`sp-${sp.id}`)}
                                      onBlur={() => { setFocusedCell(null); saveSpending(sp); }}
                                      className={numInputCls} />
                                  </td>
                                  <td className={`${cellCls} text-center`}>
                                    <select value={sp.executor || ''} onChange={e => { updateSpendingLocal(cat.id, it.id, sp.id, 'executor', e.target.value); saveSpending({ ...sp, executor: e.target.value }); }}
                                      className={centerSelectCls}>
                                      <option value="" className="bg-[#1a1428]">-</option>
                                      {EXECUTORS.map(ex => <option key={ex} value={ex} className="bg-[#1a1428]">{ex}</option>)}
                                    </select>
                                  </td>
                                  <td className={`${cellCls} text-center`}>
                                    <input type="date" value={sp.spentDate || ''} onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'spentDate', e.target.value)}
                                      onBlur={() => saveSpending(sp)}
                                      className="w-full bg-transparent text-white text-[11px] outline-none rounded px-0.5 py-0.5 text-center [color-scheme:dark]" />
                                  </td>
                                  <td className={`${cellCls} text-center`}>
                                    <select value={sp.paymentMethod || ''} onChange={e => { updateSpendingLocal(cat.id, it.id, sp.id, 'paymentMethod', e.target.value); saveSpending({ ...sp, paymentMethod: e.target.value }); }}
                                      className={centerSelectCls}>
                                      <option value="" className="bg-[#1a1428]">-</option>
                                      {METHODS.map(m => <option key={m} value={m} className="bg-[#1a1428]">{m}</option>)}
                                    </select>
                                  </td>
                                  <td className={`${cellCls} text-center`}>
                                    <select value={sp.receiptYn || '-'} onChange={e => { updateSpendingLocal(cat.id, it.id, sp.id, 'receiptYn', e.target.value); saveSpending({ ...sp, receiptYn: e.target.value }); }}
                                      className="w-full bg-transparent text-white text-xs outline-none rounded pl-1 pr-4 py-0.5 cursor-pointer text-center appearance-none">
                                      {RECEIPT_OPTIONS.map(r => <option key={r} value={r} className="bg-[#1a1428]">{r}</option>)}
                                    </select>
                                  </td>
                                  <td className={cellCls}>
                                    <input value={sp.note || ''} onChange={e => updateSpendingLocal(cat.id, it.id, sp.id, 'note', e.target.value)}
                                      onBlur={() => saveSpending(sp)} className={inputCls} placeholder="비고" />
                                  </td>
                                  <td className={`${cellCls} text-center`}>
                                    <button onClick={() => deleteSpending(cat.id, it.id, sp.id)} className="text-white/20 hover:text-red-400 transition-colors">
                                      <Trash2 size={11} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-violet-500/5">
                                <td className={cellCls}></td>
                                <td colSpan={12} className={`${cellCls} text-left`}>
                                  <button onClick={() => addSpending(cat.id, it.id)}
                                    className="flex items-center gap-1 text-white/30 hover:text-violet-400 text-[11px]">
                                    <Plus size={11} /> 세부지출 추가
                                  </button>
                                </td>
                              </tr>
                            </>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                    {items.length > 0 && (
                      <tfoot>
                        <tr className="bg-white/5 text-white/60 font-medium">
                          <td className={`${cellCls} text-right`} colSpan={7}>합계</td>
                          <td className={`${cellCls} text-right text-white`}>{fmt(totalBudget)}</td>
                          <td className={`${cellCls} text-right text-amber-400`}>{fmt(totalSpent)}</td>
                          <td colSpan={6} className={`${cellCls} text-right ${remaining < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                            잔여 {fmt(remaining)}원
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* 대분류 추가 (슈퍼관리자만) */}
        {isSuperAdmin && (showNewCat ? (
          <div className="flex items-center gap-2">
            <input
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCategory()}
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
              placeholder="대분류 이름 (예: 무대/음향, 인력비, 홍보비)"
              autoFocus
            />
            <button onClick={addCategory} className="px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm rounded-lg transition-colors">추가</button>
            <button onClick={() => { setShowNewCat(false); setNewCatName(''); }} className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white/60 text-sm rounded-lg transition-colors">취소</button>
          </div>
        ) : (
          <button onClick={() => setShowNewCat(true)}
            className="flex items-center gap-2 px-5 py-3 border border-dashed border-white/20 hover:border-violet-400/50 text-white/40 hover:text-violet-400 text-sm rounded-xl transition-colors w-full justify-center">
            <Plus size={16} /> 대분류 추가
          </button>
        ))}
      </div>
    </div>
  );
}
